import { getOwner } from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { Task, TaskInstance } from 'ember-concurrency';
import type { Insightpagesfavourite } from 'frontend-utils/models';
import type { ParityConfig } from 'lumen/models';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type { MealTypeValues } from 'lumen/utils/constants/general-constants';
import type {
  CountryCodeWithDefault,
  DateAggregation,
  Percentages,
  PercentagesWithTotal,
  PI_BREAKDOWNS,
  RateType,
} from 'lumen/utils/constants/pi-constant-values';
import DataFetching from 'lumen/utils/parity/insight/data-fetching';
import ConfigResource from 'lumen/utils/parity/resources/config';

export interface BreakdownItem {
  benchmark?: Percentages;
  calculated_values?: PercentagesWithTotal;
  children?: BreakdownItem[];
  hasNoData?: boolean;
  id: string;
  isExpanded?: boolean;
  isSelectable?: boolean;
  meta?: Record<string, string>;
  parent_id?: string;
  segmentation_id: string;
  segmentation_value: string;
  segmentation_value_id: string;
  segmentation_value_type: 'parent' | 'child';
  unfiltered_data?: Partial<BreakdownItem>;
  unfiltered_id?: string;
  value: Percentages;
}

export interface BreakdownResponse {
  all: Record<string, BreakdownItem[]>;
  issue_type: Record<string, BreakdownItem[]>;
  variance_bucket: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.BRAND_ID]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.CHANNEL]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.CHANNEL_TYPE]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.MARKET_ID]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.PLATFORM]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.SOURCE]: Record<string, BreakdownItem[]>;
  [PI_BREAKDOWNS.SUBSCRIPTION_ID]: Record<string, BreakdownItem[]>;
}

export interface BreakdownData {
  all: BreakdownItem[];
  issue_type: BreakdownItem[];
  variance_bucket: BreakdownItem[];
  [PI_BREAKDOWNS.BRAND_ID]: BreakdownItem[];
  [PI_BREAKDOWNS.CHANNEL]: BreakdownItem[];
  [PI_BREAKDOWNS.CHANNEL_TYPE]: BreakdownItem[];
  [PI_BREAKDOWNS.MARKET_ID]: BreakdownItem[];
  [PI_BREAKDOWNS.PLATFORM]: BreakdownItem[];
  [PI_BREAKDOWNS.SOURCE]: BreakdownItem[];
  [PI_BREAKDOWNS.SUBSCRIPTION_ID]: BreakdownItem[];
}

export interface HistoricalData {
  all: Record<string, BreakdownItem[]>;
}

export type ParityInsightRouteModel = {
  config: Task<ParityConfig | null, []>;
  favourites: Insightpagesfavourite[] | null;
  historicalData: TaskInstance<HistoricalData>;
  segmentationData: TaskInstance<BreakdownData>;
};

export type RouteQueryParams = {
  dateAggregation: DateAggregation;
  extractDate: string;
  historicalExtractDate: string;
  los: number;
  mealPlan: MealTypeValues;
  pos: CountryCodeWithDefault;
  rateType: RateType;
  segmentationFilters: string;
  showMemberRate: boolean;
};

export default class MarketParityInsightRoute extends Route {
  queryParams = {
    segmentationFilters: { refreshModel: true },
    historicalExtractDate: { refreshModel: true },
    extractDate: { refreshModel: true },
    benchmark: { refreshModel: false },
    dateAggregation: { refreshModel: true },
    sortType: { refreshModel: false },
    sortKey: { refreshModel: false },
    rateType: { refreshModel: true },
    los: { refreshModel: true },
    pos: { refreshModel: true },
    mealPlan: { refreshModel: true },
    showMemberRate: { refreshModel: true },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  configResource = new ConfigResource(getOwner(this)!, {
    isSingleProperty: false,
  });
  dataFetching = new DataFetching(getOwner(this)!, { isSingleProperty: false });
  lastActiveMarketId: string | null = null;
  modelObject = trackedObject<ParityInsightRouteModel>({
    config: this.configResource.fetchResourceTask,
    favourites: null,
    historicalData: {
      isRunning: true,
      value: null,
    } as TaskInstance<HistoricalData>,
    segmentationData: {
      isRunning: true,
      value: null,
    } as TaskInstance<BreakdownData>,
  });

  previousParams: RouteQueryParams | null = null;

  async afterModel(model: ParityInsightRouteModel): Promise<void> {
    // Silent fallback in case config fails to load: so page is still accessible (but validation is skipped)
    const config = await model.config.last?.catch(() => null);

    if (config) {
      this.transition.validateParityInsightQueryParamsAfter(
        this.paramsFor(this.routeName),
        config,
      );
    }
  }

  beforeModel(transition: Transition): void {
    const hasSwitchedMarket =
      this.lastActiveMarketId !== null &&
      this.lastActiveMarketId !== this.hotelAndMarketService.market.id;

    if (!this.access.hasParityInsightAccess) {
      this.transition.redirectToEnabledTab(transition);
    } else if (hasSwitchedMarket) {
      this.clearCachedData();

      // LOS, POS and filters differ per market
      const { los, pos, segmentationFilters } =
        DEFAULT_QP_VALUES.market.parity.insight;
      this.router.transitionTo({
        queryParams: { los, pos, segmentationFilters },
      });
    } else {
      this.transition.validateParityInsightQueryParamsBefore(
        this.paramsFor(this.routeName),
      );
    }
  }

  clearCachedData(): void {
    this.dataFetching.clearCachedData();

    this.modelObject.favourites = null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: RouteQueryParams) {
    // Check whether QPs have changes. On initial load previousParams is null and the checks below should all pass
    const isHistoricalExtractDateUpdate =
      this.previousParams === null ||
      params.historicalExtractDate !==
        this.previousParams.historicalExtractDate;

    const isExtractDateUpdate =
      this.previousParams === null ||
      params.extractDate !== this.previousParams.extractDate;

    const isDateAggregationUpdate =
      this.previousParams === null ||
      params.dateAggregation !== this.previousParams.dateAggregation;

    const isSelectionsUpdate =
      params.showMemberRate !== this.previousParams?.showMemberRate ||
      params.los !== this.previousParams?.los ||
      params.pos !== this.previousParams?.pos ||
      params.mealPlan !== this.previousParams?.mealPlan ||
      params.rateType !== this.previousParams?.rateType;

    // Check whether only filters have been changed so we can use storedUnfilteredResponse (no extra request required)
    // When all filters all filters are cleared (length of filters = 0) we can return false as we don't need to make a filtered request
    const isFilterUpdate =
      params.segmentationFilters.length &&
      params.segmentationFilters !== this.previousParams?.segmentationFilters;

    this.previousParams = params;

    // Fetch config once on first page load
    if (this.lastActiveMarketId !== this.hotelAndMarketService.market.id) {
      void this.modelObject.config.perform();
    }
    this.lastActiveMarketId = this.hotelAndMarketService.hotelOrMarket.id;

    if (this.modelObject.favourites === null) {
      const parameters = {
        market: this.hotelAndMarketService.hotelOrMarket.id!,
        page_type: 'PA',
      };

      void this.store
        .query<Insightpagesfavourite>('insightpagesfavourite', parameters)
        .then((favourites) => {
          this.modelObject.favourites = [...favourites];
        });
    }

    // If there is no cache (page load) or all updates are required, just do EVERYTHING!
    const requiresAllUpdates =
      (isHistoricalExtractDateUpdate && isExtractDateUpdate) ||
      isSelectionsUpdate ||
      isDateAggregationUpdate;

    if (!this.dataFetching.unfilteredCache || requiresAllUpdates) {
      // Request filtered/unfiltered historical data with general function which will decide which request to make for us
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);

      // Load segmentation data filtered/unfiltered using general function that merges/caches it for us
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataTask.perform(params);

      // Special case: If you have a page load with filters, make sure to also cache in the background the unfiltered version for it
      if (this.dataFetching.hasActiveFilters(params)) {
        void this.dataFetching.requestUnfilteredHistoricalDataTask.perform(
          params,
        );
      }
    } else if (isFilterUpdate) {
      // If it's simply a filter update - we want a filtered request for all data
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataFromCacheTask.perform(params);

      // Filter update situation also requires new historical data
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);
    } else if (isExtractDateUpdate) {
      // If there was only an extract date update - don't update that history chart, that data doesn't change!
      this.modelObject.segmentationData =
        this.dataFetching.requestSegmentationDataTask.perform(params);

      // If filtered request, return appropriate cache item
      if (this.dataFetching.hasActiveFilters(params)) {
        this.modelObject.historicalData = {
          value: this.dataFetching.filteredHistoricalCache,
        } as TaskInstance<HistoricalData>;
      } else {
        this.modelObject.historicalData = {
          value: this.dataFetching.unfilteredHistoricalCache,
        } as TaskInstance<HistoricalData>;
      }
    } else if (isHistoricalExtractDateUpdate) {
      // If there is a historical update - only update that history chart
      this.modelObject.historicalData =
        this.dataFetching.requestHistoricalDataTask(params);

      // When filters active, and changing the scope. Also cache the unfiltered version in the background. Otherwise removing filters results in changing history data
      if (this.dataFetching.hasActiveFilters(params)) {
        void this.dataFetching.requestUnfilteredHistoricalDataTask.perform(
          params,
        );
        this.modelObject.segmentationData = {
          value: this.dataFetching.filteredCache,
        } as TaskInstance<BreakdownData>;
      } else {
        this.modelObject.segmentationData = {
          value: this.dataFetching.unfilteredCache,
        } as TaskInstance<BreakdownData>;
      }
    } else if (
      this.dataFetching.filteredCache &&
      this.dataFetching.filteredHistoricalCache &&
      this.dataFetching.hasActiveFilters(params)
    ) {
      // If there is no update and there are caches, then return those caches if they are available, otherwise we return nothing
      this.modelObject.segmentationData = {
        value: this.dataFetching.filteredCache,
      } as TaskInstance<BreakdownData>;
      this.modelObject.historicalData = {
        value: this.dataFetching.filteredHistoricalCache,
      } as TaskInstance<HistoricalData>;
    } else if (
      this.dataFetching.unfilteredCache &&
      this.dataFetching.unfilteredHistoricalCache
    ) {
      this.modelObject.segmentationData = {
        value: this.dataFetching.unfilteredCache,
      } as TaskInstance<BreakdownData>;
      this.modelObject.historicalData = {
        value: this.dataFetching.unfilteredHistoricalCache,
      } as TaskInstance<HistoricalData>;
    }

    return this.modelObject;
  }
}
